import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Mail, Settings, Bell, 
  LogOut, Menu, X, ChevronRight, BarChart3, MessageSquare,
  CreditCard, BookOpen, GraduationCap, UserCheck, Folder, FileQuestion,
  Building2, Newspaper, KeyRound
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import ChangePasswordModal from './ChangePasswordModal';
import NotificationBell from './NotificationBell';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_e21507e1-a0e4-40f2-a327-b19a660901a9/artifacts/o5ssfxmc_english%20hero.png';

const AdminLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/login');
      return;
    }
    loadStats();
  }, [isAdmin, navigate]);

  const loadStats = async () => {
    try {
      const [statsRes, infoRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/settings/site-info'),
      ]);
      setStats({ ...statsRes.data, ...infoRes.data });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const messagingEnabled = stats.messaging_enabled !== false;
  const forumEnabled = stats.forum_enabled !== false;

  const menuItems = [
    { name: 'Tableau de bord', icon: LayoutDashboard, path: '/admin' },
    { name: 'Inscriptions', icon: FileText, path: '/admin/inscriptions', badge: stats.pending_inscriptions },
    { name: 'Demandes de devis', icon: FileQuestion, path: '/admin/devis', badge: stats.total_devis },
    { name: 'Demandes élèves', icon: UserCheck, path: '/admin/eleve-requests', badge: stats.eleves_en_attente },
    { name: 'Gestion élèves', icon: Users, path: '/admin/eleves' },
    { name: 'Groupes', icon: GraduationCap, path: '/admin/groupes' },
    { name: 'Tarifs & Frais', icon: CreditCard, path: '/admin/tarifs' },
    { name: 'Blog', icon: Newspaper, path: '/admin/blog' },
    ...(messagingEnabled
      ? [{ name: 'Messagerie interne', icon: Mail, path: '/admin/messaging' }]
      : []),
    { name: 'Messages contact', icon: MessageSquare, path: '/admin/messages', badge: stats.unread_messages },
    { name: 'Communications', icon: Bell, path: '/admin/communications' },
    { name: 'Contenu CMS', icon: BookOpen, path: '/admin/cms' },
    { name: 'Paramètres', icon: Settings, path: '/admin/settings' },
  ];

  // Espace Agence visible uniquement pour le rôle "agence"
  const agenceMenuItem = { name: 'Espace Agence', icon: Building2, path: '/admin/agence', highlight: true };

  // Filter menu items based on user role
  const filteredMenuItems = user?.role === 'agence' 
    ? [...menuItems, agenceMenuItem]
    : menuItems;

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2C3C65] transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-4 py-5 border-b border-white/10">
            <Link to="/admin" className="flex items-center gap-2">
              <img
                src={LOGO_URL}
                alt="The English Speaking Center"
                className="h-12 w-auto object-contain"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
              <span className="hidden items-center text-white font-bold">
                <GraduationCap className="h-6 w-6 mr-2" />
                TESC
              </span>
            </Link>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-[#285BBB] text-white'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`admin-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </div>
                  {item.badge > 0 && (
                    <span className="bg-[#ff3a2d] text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-[#285BBB] rounded-full flex items-center justify-center text-white font-semibold">
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </div>
              <div>
                <p className="text-white font-medium text-sm">{user?.prenom} {user?.nom}</p>
                <p className="text-gray-400 text-xs">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center w-full px-4 py-2 mb-1 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              data-testid="admin-change-password"
            >
              <KeyRound className="h-5 w-5 mr-3" />
              Mot de passe
            </button>
            <button
              onClick={logout}
              className="flex items-center w-full px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>

      <ChangePasswordModal open={showPasswordModal} onClose={() => setShowPasswordModal(false)} />

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-4 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex-1 ml-4 lg:ml-0">
              <h1 className="text-xl font-semibold text-[#2C3C65]">
                Administration - The English Speaking Center
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Link
                to="/"
                className="text-sm text-[#285BBB] hover:underline flex items-center"
              >
                Voir le site
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
